
/**
 * <peppermill-calendar> is a general purpose hotel date selection widget.
 * Listen to `change` event on `.calendar--container` node or for `change` $emit in Vue.
 * Dispatch `invalid-date` event to root node to add an invalid date to the component. Set date as YYYY-mm-dd in event.detail.
 * Dispatch `set-selection-start` event to root node to change the selection start date. Set date as YYYY-mm-dd in event.detail.
 * Dispatch `set-selection-end` event to root node to change the selection end date. Set date as YYYY-mm-dd in event.detail.
 * Dispatch `set-offer-type` event to root node to change the offer type metadata for the component. If set to "casino", a message about comp dates will display.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.0
 * @changelog
 *  2023-01-09 (1.0.0) (jhoward)
 *    - <peppermill-calendar> dynamically builds a calendar based on start/end dates.
 *    - <peppermill-calendar> can handle dynamic invalid dates.
 *    - <peppermill-calendar> $emits (and dispatches) a `change` event when start or end date changes.
 *
 *  2023-02-23 (1.0.1) (jhoward)
 *    - Add date string normalization in <peppermill-calendar>::getTimezoneNormalizedDate()
 *
 *  2023-03-09 (1.1.0) (jhoward)
 *    - <peppermill-calendar> will not allow dates selected when invalid dates are between the start date and the attempted selected date.
 *    - <peppermill-calendar> calculates the first available date range when invalid dates are added that invalidate the current selection.
 *    - <peppermill-calendar> emits `error` with a value of 'no-dates-available' when invalid dates are supplied and no date range is available.
 *
 *  2023-03-21 (1.1.1) (jhoward)
 *    - Indicate if dates were chosen by the user when emitting the `change` event in <peppermill-calendar> by sending `userSelected` data.
 *    - Export PeppermillCalendarChangeEventDetail interface in <peppermill-calendar>.
 *
 *  2023-06-08 (1.2.0) (jhoward)
 *    - Fix end months computed property by evaluating "currentMonth" as the first of each month.
 */

import {defineComponent} from "vue";
import MaterialButton from "./material/MaterialButton.vue";

interface InvalidCalendarDate {
  key: string;
  date: Date;
}

interface CalendarDay {
  key: string;
  title: string;
  date: Date | null;
  isValid: boolean;
}

interface CalendarWeek {
  key: string;
  days: CalendarDay[];
}

interface CalendarMonth {
  key: string;
  title: string;
  year: number;
  month: number;
  weeks: CalendarWeek[];
}

export interface PeppermillCalendarChangeEventDetail {
  start: string | null;
  end: string | null;
  userSelected: boolean;
}

export default defineComponent({
  name: "PeppermillCalendar",
  components: {MaterialButton},
  emits: ['change', 'error'],
  props: {
    start: {
      type: String,
      required: true
    },
    end: {
      type: String,
      required: true
    },
    minDays: {
      type: Number,
      default: 1
    },
    maxDays: {
      type: Number,
      default: 14
    },
    widthBreakpoint: {
      type: Number,
      default: 400
    },
    initialSelectionStartDate: {
      type: String,
      default: ''
    },
    initialSelectionEndDate: {
      type: String,
      default: ''
    },
    initialOfferType: {
      type: String,
      default: ''
    },
    hotelPhoneNumber: {
      type: String,
      default: '800-648-5555'
    }
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.widgetWidth = this.$el.clientWidth;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.widgetWidth = this.$el.clientWidth;
    }, {passive: true});
    this.widgetWidth = this.$el.clientWidth;

    this.$el.addEventListener('invalid-date', (event: CustomEvent) => {
      if (event.detail) {
        this.setInvalidDate(event.detail);
      }
    });

    this.$el.addEventListener('set-offer-type', (event: CustomEvent) => {
      if (event.detail) {
        this.offerType = event.detail;
      }
    });

    this.$el.addEventListener('set-selection-start', (event: CustomEvent) => {
      if (event.detail) {
        this.selectedStartDate = this.getTimezoneNormalizedDate(event.detail);
        this.recomputeMonthsCounter++;
      }
    });
    this.$el.addEventListener('set-selection-end', (event: CustomEvent) => {
      if (event.detail) {
        this.selectedEndDate = this.getTimezoneNormalizedDate(event.detail);
        this.recomputeMonthsCounter++;
      }
    });

    if (!!this.initialSelectionStartDate && !!this.initialSelectionStartDate.length) {
      this.selectedStartDate = this.getTimezoneNormalizedDate(this.initialSelectionStartDate);
      this.recomputeMonthsCounter++;
    }
    if (!!this.initialSelectionEndDate && !!this.initialSelectionEndDate.length) {
      this.selectedEndDate = this.getTimezoneNormalizedDate(this.initialSelectionEndDate);
      this.recomputeMonthsCounter++;
    }

    this.setVisibleMonths(0);
    this.setInvalidDateTimeout();
  },
  data() {
    return {
      widgetWidth: 0 as number,

      visibleMonths: [] as number[],

      invalidDates: {} as { [key: string]: InvalidCalendarDate },
      invalidDateTimer: -1 as number,
      isAnyDatesAvailable: true,

      selectedStartDate: null as Date | null,
      selectedEndDate: null as Date | null,

      lastSelectionByUserTimer: -1,
      isLastSelectionByUser: false,

      recomputeMonthsCounter: 0 as number,
      recomputeMonthsTimer: -1 as number,

      changeEventTimer: -1,

      language: {
        en: {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'mm/dd/yyyy',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      },

      messages: {
        minDays: {
          isActive: false,
          html: `This offer requires a minimum of <strong>${this.minDays}</strong> nights.`
        },
        maxDays: {
          isActive: false,
          html: `This offer allows a maximum of <strong>${this.maxDays}</strong> nights.`
        },
        compDays: {
          isActive: false,
          html: `Consecutive hotel offers require one paid day between stays. Please call Reservations at ${this.hotelPhoneNumber} for assistance.`
        }
      },

      offerType: this.initialOfferType
    };
  },
  computed: {
    isFirstMonthShowing(): boolean {
      return (!this.visibleMonths.length || 0 === this.visibleMonths[0]);
    },
    isLastMonthShowing(): boolean {
      return (!this.visibleMonths.length || this.months.length - 1 === this.visibleMonths[this.visibleMonths.length - 1]);
    },

    startDate(): Date {
      let date = this.getTimezoneNormalizedDate(this.start);
      if (!isNaN(date.getTime())) {
        return date;
      }

      return new Date();
    },
    endDate(): Date {
      let date = this.getTimezoneNormalizedDate(this.end);
      if (!isNaN(date.getTime())) {
        return date;
      }

      date = new Date();

      return this.addMonths(new Date(date.getFullYear(), date.getMonth() - 1, 1), 12);
    },
    months(): CalendarMonth[] {
      this.recomputeMonthsCounter;

      let currentMonth = this.getFirstDateOfMonth(this.startDate.getFullYear(), this.startDate.getMonth());
      const output: CalendarMonth[] = [];
      let counter = 0;
      while (currentMonth < this.endDate && counter++ < 64) {
        output.push(
            {
              key: currentMonth.getFullYear() + '-' + (currentMonth.getMonth() + 1),
              title: this.language.en.months[currentMonth.getMonth()] + ' ' + currentMonth.getFullYear(),
              month: currentMonth.getMonth() + 1,
              year: currentMonth.getFullYear(),
              weeks: this.getMonthWeeks(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
            }
        );
        currentMonth = this.addMonths(currentMonth, 1);
      }

      return output;
    },
    containerStyles() {
      return {
        '--width-breakpoint': this.getBreakpointMultiple()
      };
    },
  },
  watch: {
    isAnyDatesAvailable() {
      if (!this.isAnyDatesAvailable) {
        this.$emit('error', 'no-dates-available');
        this.$el.dispatchEvent(new CustomEvent('error', {detail: 'no-dates-available', bubbles: true}));
      }
    },
    offerType() {
      this.messages.compDays.isActive = !!('casino' === this.offerType && Object.keys(this.invalidDates).length);
    },
    invalidDates: {
      deep: true,
      handler() {
        clearTimeout(this.recomputeMonthsTimer);
        this.recomputeMonthsTimer = window.setTimeout(() => {
          if (!!this.selectedStartDate && !!this.selectedEndDate && this.isDateRangeInvalid(this.selectedStartDate, this.selectedEndDate)) {
            //move reservation to first available date range
            let currentDate = this.addDays(this.selectedStartDate, 1);
            const stayLength = this.daysBetween(this.selectedStartDate, this.selectedEndDate);
            let counter = 0;
            while (this.isDateBefore(currentDate, this.endDate) && ++counter < 4) {
              const newSelectedEndDate = this.addDays(currentDate, stayLength);
              if (!this.isDateRangeInvalid(currentDate, newSelectedEndDate)) {
                this.selectedStartDate = currentDate;
                this.selectedEndDate = newSelectedEndDate;
                break;
              }
              currentDate = this.addDays(currentDate, 1);
            }
          } else if (!!this.selectedStartDate && this.getDateKey(this.selectedStartDate) in this.invalidDates) {
            //reset start date as it is now invalid
            this.selectedStartDate = null;
          } else if (!!this.selectedEndDate && this.getDateKey(this.selectedEndDate) in this.invalidDates) {
            //reset end date as it is now invalid
            this.selectedEndDate = null;
          }

          this.messages.compDays.isActive = !!('casino' === this.offerType && Object.keys(this.invalidDates).length);
          this.recomputeMonthsCounter++;
        }, 132);
      }
    },
    widgetWidth(newValue, oldValue): void {
      const oldBreakpointMultiple = this.getBreakpointMultiple(oldValue);
      const newBreakpointMultiple = this.getBreakpointMultiple(newValue);
      if (oldBreakpointMultiple !== newBreakpointMultiple) {
        this.setVisibleMonths();
      }
    },
    selectedStartDate() {
      clearTimeout(this.changeEventTimer);
      this.changeEventTimer = window.setTimeout(this.sendChangeEvent, 16);
    },
    selectedEndDate() {
      clearTimeout(this.changeEventTimer);
      this.changeEventTimer = window.setTimeout(this.sendChangeEvent, 16);
    },
  },
  methods: {
    getDateKey(date: Date): string {
      if (!isNaN(date.getTime())) {
        return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
      }

      return '';
    },
    isDateRangeInvalid(startDate: Date, endDate: Date): boolean {
      let currentDate = startDate;
      while (this.isDateBefore(currentDate, endDate, true)) {
        if (this.getDateKey(currentDate) in this.invalidDates || this.isDateBefore(currentDate, this.startDate) || this.isDateAfter(currentDate, this.endDate)) {
          return true;
        }
        currentDate = this.addDays(currentDate, 1);
      }

      return false;
    },
    isDayCheckInOrOut(day: CalendarDay): boolean {
      return !!day.date
          && (
              !!this.selectedStartDate && this.isMatchingDates(day.date, this.selectedStartDate)
              || !!this.selectedEndDate && this.isMatchingDates(day.date, this.selectedEndDate)
          );
    },
    getBreakpointMultiple(width?: number) {
      if ('number' !== typeof width) {
        width = this.widgetWidth;
      }
      return Math.ceil(width / this.widthBreakpoint);
    },

    sendChangeEvent() {
      const changeValue = {start: this.selectedStartDate, end: this.selectedEndDate, userSelected: this.isLastSelectionByUser};
      this.$emit('change', changeValue);
      this.$el.dispatchEvent(new CustomEvent('change', {detail: JSON.stringify(changeValue), bubbles: true}));
    },

    dayClasses(day: CalendarDay) {
      const isCheckInDate = !!day.date && !!this.selectedStartDate && this.isMatchingDates(day.date, this.selectedStartDate);
      const isCheckOutDate = !!day.date && !!this.selectedEndDate && this.isMatchingDates(day.date, this.selectedEndDate);
      return {
        'day--container-invalid': !this.isDayValid(day),
        'day--container-check_in': isCheckInDate,
        'day--container-check_out': isCheckOutDate,
        'day--container-highlight': (
            !!day.date
            && (
                isCheckInDate
                || isCheckOutDate
                || (
                    !!this.selectedStartDate
                    && !!this.selectedEndDate
                    && this.isDateAfter(day.date, this.selectedStartDate)
                    && this.isDateBefore(day.date, this.selectedEndDate)
                )
            )
        )
      };
    },
    isDayValid(day: CalendarDay): boolean {
      if (!day.isValid || !day.date) {
        return false;
      } else if (this.minDays > 0 && this.selectedStartDate && !this.selectedEndDate) {
        //check minDays
        const minDaysStart = this.addDays(this.selectedStartDate, -1 * this.minDays);
        const minDaysEnd = this.addDays(this.selectedStartDate, this.minDays);
        if (this.isDateAfter(day.date, minDaysStart) && this.isDateBefore(day.date, minDaysEnd) && !this.isMatchingDates(day.date, this.selectedStartDate)) {
          this.messages.minDays.isActive = true;
          return false;
        }

        //check maxDays
        const maxDaysStart = this.addDays(this.selectedStartDate, -1 * this.maxDays);
        const maxDaysEnd = this.addDays(this.selectedStartDate, this.maxDays);
        if (this.isDateBefore(day.date, maxDaysStart) || this.isDateAfter(day.date, maxDaysEnd)) {
          this.messages.maxDays.isActive = true;
          return false;
        }

        //check for invalid dates between `day` and the check in date
        if (this.isDateRangeContainingInvalidDate(day.date, this.selectedStartDate)) {
          return false;
        }
      }

      return true;
    },

    getTimezoneNormalizedDate(date: string): Date {
      const dateMatches = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(date);
      let normalizedDateString = date;
      if (dateMatches) {
        normalizedDateString = dateMatches[1] + '-';
        if (dateMatches[2].length === 1) {
          normalizedDateString += '0';
        }
        normalizedDateString += dateMatches[2] + '-';

        if (dateMatches[3].length === 1) {
          normalizedDateString += '0';
        }
        normalizedDateString += dateMatches[3];
      }

      const timeZoneSuffix = (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDateString)) ? 'T00:00:00-08:00' : '';
      return new Date(normalizedDateString + timeZoneSuffix);
    },

    setInvalidDate(date: Date | string | number): void {
      if ('string' === typeof date) {
        date = this.getTimezoneNormalizedDate(date);
      } else if (!(date instanceof Date)) {
        date = new Date(date);
      }
      if (!isNaN(date.getTime())) {
        const dateKey = this.getDateKey(date);
        this.invalidDates[dateKey] = {
          key: dateKey,
          date: date
        };

        this.setInvalidDateTimeout();
      }
    },
    setInvalidDateTimeout(): void {
      clearTimeout(this.invalidDateTimer);
      this.invalidDateTimer = window.setTimeout(this.invalidDateCallback, 132);
    },
    invalidDateCallback(): void {
      if (!this.selectedStartDate || !this.selectedEndDate) {
        this.setInvalidDateTimeout();
      } else if (
          !this.isDateRangeValid(this.selectedStartDate, this.selectedEndDate)
          || this.isDateRangeContainingInvalidDate(this.selectedStartDate, this.selectedEndDate)
      ) {
        let stayLength = this.daysBetween(this.selectedStartDate, this.selectedEndDate);
        let newDatesFound = false;
        while (stayLength >= this.minDays && stayLength > 0) {
          const firstAvailableDates = this.getFirstValidDateRange(stayLength);

          if (firstAvailableDates) {
            this.selectedStartDate = firstAvailableDates.start;
            this.selectedEndDate = firstAvailableDates.end;
            newDatesFound = true;
            break;
          }

          stayLength--;
        }

        if (!newDatesFound) {
          this.isAnyDatesAvailable = false;
          this.selectedStartDate = null;
          this.selectedEndDate = null;
        } else {
          this.isAnyDatesAvailable = true;
        }
      }
    },
    getFirstValidDateRange(stayLength?: number): {start: Date, end: Date}|null {
      if (this.selectedStartDate && this.selectedEndDate) {
        const lastDate = this.getLastDate();
        const firstDate = this.getFirstDate();
        if ('number' !== typeof stayLength) {
          stayLength = this.daysBetween(this.selectedStartDate, this.selectedEndDate);
        }
        let startDate = firstDate;
        let endDate = this.addDays(startDate, stayLength);
        while (this.isDateRangeContainingInvalidDate(startDate, endDate)) {
          startDate = this.addDays(startDate, 1);
          endDate = this.addDays(startDate, stayLength);
          if (!this.isDateBefore(endDate, lastDate, true)) {
            return null;
          }
        }

        return {
          start: startDate,
          end: endDate
        };
      }

      return null;
    },
    getFirstDate(): Date {
      for (let monthIndex = 0; monthIndex < this.months.length; monthIndex++) {
        for (let weekIndex = 0; weekIndex < this.months[monthIndex].weeks.length; weekIndex++) {
          for (let dayIndex = 0; dayIndex < this.months[monthIndex].weeks[weekIndex].days.length; dayIndex++) {
            if (this.isDayValid(this.months[monthIndex].weeks[weekIndex].days[dayIndex])) {
              //ignore because this.isDayValid will make sure the date is indeed a Date object
              //@ts-ignore
              return this.months[monthIndex].weeks[weekIndex].days[dayIndex].date;
            }
          }
        }
      }

      return new Date();
    },
    getLastDate(): Date {
      for (let monthIndex = this.months.length - 1; monthIndex >= 0; monthIndex--) {
        for (let weekIndex = this.months[monthIndex].weeks.length - 1; weekIndex >= 0; weekIndex--) {
          for (let dayIndex = this.months[monthIndex].weeks[weekIndex].days.length - 1; dayIndex >= 0; dayIndex--) {
            if (this.isDayValid(this.months[monthIndex].weeks[weekIndex].days[dayIndex])) {
              //ignore because this.isDayValid will make sure the date is indeed a Date object
              //@ts-ignore
              return this.months[monthIndex].weeks[weekIndex].days[dayIndex].date;
            }
          }
        }
      }

      return new Date();
    },
    isDateRangeValid(startDate: Date, endDate: Date): boolean {
      //is the startDate before the first available date on the calendar?
      if (startDate.getTime() < this.getFirstDate().getTime()) {
        return false;
      }

      //is the endDate after the last available date on the calendar?
      if (endDate.getTime() > this.getLastDate().getTime()) {
        return false;
      }

      return true;
    },
    isDateRangeContainingInvalidDate(startDate: Date, endDate: Date): boolean {
      //make sure endDate is NOT before startDate
      if (endDate.getTime() < startDate.getTime()) {
        const tmpEndDate = endDate;
        endDate = startDate;
        startDate = tmpEndDate;
      }

      //loop through date range and check for invalid date
      let loopDate = startDate;
      let counter = 0;
      while (loopDate.getTime() < endDate.getTime() && ++counter < 20) {
        const dateKey = this.getDateKey(loopDate);
        if (dateKey in this.invalidDates) {
          return true;
        }
        loopDate = this.addDays(loopDate, 1);
      }

      return false;
    },

    setSelectedDate(day: CalendarDay, isUserSelection?: boolean): void {
      if (this.isDayValid(day) && !!day.date) {
        //only work with valid dates
        if (!isNaN(day.date.getTime())) {
          //reset minDays and maxDays messages
          this.messages.minDays.isActive = false;
          this.messages.maxDays.isActive = false;

          if ('boolean' === typeof isUserSelection && isUserSelection) {
            clearTimeout(this.lastSelectionByUserTimer);
            this.isLastSelectionByUser = true;
            this.lastSelectionByUserTimer = window.setTimeout(() => {this.isLastSelectionByUser = false;}, 132);
          }

          //start over if both start and end dates were already set
          if (!!this.selectedStartDate && this.isMatchingDates(this.selectedStartDate, day.date) && !this.selectedEndDate) {
            this.selectedStartDate = null;
          } else {
            if (!!this.selectedStartDate && !!this.selectedEndDate) {
              this.selectedStartDate = null;
              this.selectedEndDate = null;
            }

            //set start or end date based on current start/end dates
            if (!this.selectedStartDate) {
              this.selectedStartDate = day.date;
            } else if (!this.selectedEndDate && !this.isMatchingDates(day.date, this.selectedStartDate)) {
              this.selectedEndDate = day.date;
            }

            //reverse dates if end date is before start date
            if (!!this.selectedStartDate && !!this.selectedEndDate && this.isDateAfter(this.selectedStartDate, this.selectedEndDate)) {
              const selectedEndDateSave = this.selectedEndDate;
              this.selectedEndDate = this.selectedStartDate;
              this.selectedStartDate = selectedEndDateSave;
            }
          }
        }
      }
    },

    setPreviousVisibleMonths(): void {
      if (!this.isFirstMonthShowing) {
        if (this.visibleMonths.length) {
          this.setVisibleMonths(this.visibleMonths[0] - 1);
        } else {
          this.setVisibleMonths(0);
        }
      }
    },
    setNextVisibleMonths(): void {
      if (!this.isLastMonthShowing) {
        if (this.visibleMonths.length) {
          this.setVisibleMonths(this.visibleMonths[0] + 1);
        } else {
          this.setVisibleMonths(0);
        }
      }
    },
    setVisibleMonths(monthIndex?: number): void {
      if ('number' !== typeof monthIndex) {
        monthIndex = this.visibleMonths[0] || 0;
      }

      if (monthIndex < 0) {
        monthIndex = 0;
      } else if (monthIndex > this.months.length - 1) {
        monthIndex = this.months.length - 1;
      }

      this.visibleMonths = [];
      const breakpointMultiple = this.getBreakpointMultiple();
      for (let i = 0; i < breakpointMultiple && i < this.months.length; i++) {
        this.visibleMonths.push(monthIndex + i);
      }
      // this.visibleMonths = [monthIndex];
      // if (this.widgetWidth > this.widthBreakpoint && this.months.length >= monthIndex + 1) {
      //   this.visibleMonths.push(monthIndex + 1);
      // }
    },

    getMonthWeeks(year: number, month: number): CalendarWeek[] {
      const weeks: CalendarWeek[] = [];
      const monthDate = new Date(year, month - 1, 1);

      for (let i = 0; i < 7; i++) {
        const daysInWeek = this.getWeekDays(monthDate, i);
        if (daysInWeek) {
          weeks.push({
            key: year + '-' + month + '-' + i,
            days: daysInWeek
          });
        } else {
          break;
        }
      }

      return weeks;
    },
    getWeekDays(monthDate: Date, weekIndex: number): CalendarDay[] {
      const days: CalendarDay[] = [];
      const daysInMonth = this.getDaysInMonth(monthDate);
      if (daysInMonth >= weekIndex * 7 - monthDate.getDay()) {
        for (let i = 0; i < 7; i++) {
          const dateInMonth = weekIndex * 7 - monthDate.getDay() + 1 + i;
          const dateKey = monthDate.getFullYear() + '-' + (monthDate.getMonth() + 1) + '-' + dateInMonth;
          if (
              dateInMonth < 1
              || dateInMonth > daysInMonth
              || (
                  monthDate.getFullYear() === this.startDate.getFullYear()
                  && monthDate.getMonth() === this.startDate.getMonth()
                  && dateInMonth < this.startDate.getDate()
              )
              || (
                  monthDate.getFullYear() === this.endDate.getFullYear()
                  && monthDate.getMonth() === this.endDate.getMonth()
                  && dateInMonth > this.endDate.getDate()
              )
          ) {
            days.push({
              key: dateKey,
              isValid: false,
              title: (dateInMonth > 0 && dateInMonth <= daysInMonth) ? '' + dateInMonth : '',
              date: null
            });
          } else {
            //date exists
            const date = this.getTimezoneNormalizedDate('' + monthDate.getFullYear() + '-' + (monthDate.getMonth() + 1) + '-' + dateInMonth);
            days.push({
              key: dateKey,
              title: '' + dateInMonth,
              date,
              isValid: !(dateKey in this.invalidDates)
            });
          }
        }
      }

      return days;
    },


    isMatchingDates(date1: Date, date2: Date) {
      return date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate();
    },
    isDateAfter(afterDate: Date, beforeDate: Date, equalTo?: boolean) {
      return this.isDateBefore(beforeDate, afterDate, equalTo || false);
    },
    isDateBefore(beforeDate: Date, afterDate: Date, equalTo?: boolean) {
      if ('boolean' !== typeof equalTo) {
        equalTo = false;
      }
      const beforeDay = (beforeDate.getDate() < 10) ? '0' + beforeDate.getDate() : beforeDate.getDate();
      const afterDay = (afterDate.getDate() < 10) ? '0' + afterDate.getDate() : afterDate.getDate();
      const beforeMonth = (beforeDate.getMonth() + 1 < 10) ? '0' + (beforeDate.getMonth() + 1) : (beforeDate.getMonth() + 1);
      const afterMonth = (afterDate.getMonth() + 1 < 10) ? '0' + (afterDate.getMonth() + 1) : (afterDate.getMonth() + 1);
      const beforeDateNumber = parseInt('' + beforeDate.getFullYear() + beforeMonth + beforeDay, 10);
      const afterDateNumber = parseInt('' + afterDate.getFullYear() + afterMonth + afterDay, 10);

      if (equalTo) {
        // console.log(beforeDateNumber, afterDateNumber, beforeDateNumber <= afterDateNumber, beforeDate.getTime() <= afterDate.getTime());
        return beforeDateNumber <= afterDateNumber;
      }

      // console.log(beforeDateNumber, afterDateNumber, beforeDateNumber < afterDateNumber, beforeDate.getTime() < afterDate.getTime());
      return beforeDateNumber < afterDateNumber;
    },
    getDaysInMonth(date: Date): number {
      return this.addDays(this.addMonths(date, 1), -1).getDate();
    },
    treatAsUTC(date: Date): Date {
      let result = new Date(date);

      result.setMinutes(result.getMinutes() - result.getTimezoneOffset());

      return result;
    },
    daysBetween(startDate: Date, endDate: Date): number {
      let millisecondsPerDay = 24 * 60 * 60 * 1000;

      return (this.treatAsUTC(endDate).getTime() - this.treatAsUTC(startDate).getTime()) / millisecondsPerDay;
    },
    addMinutes(date: Date, minutes: number): Date {
      let result = new Date(date);

      result.setMinutes(result.getMinutes() + minutes);

      return result;

    },
    addDays(date: Date, days: number): Date {
      let result = new Date(date);

      result.setDate(result.getDate() + days);

      return result;
    },
    addMonths(date: Date, months: number): Date {
      let result = new Date(date);

      result.setMonth(result.getMonth() + months);

      return result;
    },
    monthsBetween(d1: Date, d2: Date): number {
      let months = (d2.getFullYear() - d1.getFullYear()) * 12;

      months -= d1.getMonth();
      months += d2.getMonth();

      return (months <= 0)
          ? 0
          : months;
    },
    formatYYYYMMDDToDateObject(YYYYMMDD: string): Date | null {
      let matches = /(\d{4})-(\d{2})-(\d{2})/.exec(YYYYMMDD);
      if (matches) {
        let date = new Date();
        date.setFullYear(parseInt(matches[1], 10));
        date.setMonth(parseInt(matches[2], 10) - 1);
        date.setDate(parseInt(matches[3], 10));

        return date;
      }

      return null;
    },
    formatDateObjectYYYYMMDD(dateObject: Date): string {
      let regMatch;

      // check for valid date
      if (typeof dateObject === 'object' && typeof dateObject.getMonth === 'function') {
        regMatch = /(20[0-9]{2})-([0-9]{1,2})-([0-9]{1,2})/.exec(dateObject.getFullYear() + '-' + (dateObject.getMonth() + 1) + '-' + dateObject.getDate());

        if (regMatch) {
          // verify correct format for months
          if (regMatch[2].length < 2) regMatch[2] = '0' + regMatch[2];

          // verify correct format for days
          if (regMatch[3].length < 2) regMatch[3] = '0' + regMatch[3];

          return regMatch[1] + '-' + regMatch[2] + '-' + regMatch[3];
        }
      }

      return '';
    },
    dateSuffix(dayOfMonth: number): string {
      return (-1 !== [1, 21, 31].indexOf(dayOfMonth))
          ? 'st'
          : (-1 !== [2, 22].indexOf(dayOfMonth))
              ? 'nd'
              : (-1 !== [3, 23].indexOf(dayOfMonth))
                  ? 'rd'
                  : 'th';
    },
    getFirstDateOfMonth(year: number, month: number): Date {
      return new Date(year, month, 1);
    }
  }
})
